import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FloppyDisk, Download, ArrowClockwise, Trash, Upload, Warning, CheckCircle, ShieldCheck } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('sr-Latn-ME', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/backup/list`, { headers: headers() });
      setBackups(res.data.items || []);
      setRetention(res.data.retention || 30);
    } catch (err) {
      toast.error('Greška pri učitavanju backup-a');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  // ---- Manual backup: download directly to user's machine ----
  const handleDownloadNow = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/backup/create-download`,
        {},
        { headers: headers(), responseType: 'blob' }
      );
      // Read filename from Content-Disposition if present
      let filename = 'crmfinance_backup.zip';
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup preuzet: ${filename}`);
    } catch (err) {
      toast.error('Greška pri kreiranju backup-a: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setDownloading(false);
    }
  };

  // ---- Save backup to server (kept alongside auto-backups) ----
  const handleSaveToServer = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/backup/create-stored`, {}, { headers: headers() });
      toast.success(`Sačuvan na serveru: ${res.data.filename} (${formatBytes(res.data.size_bytes)})`);
      await fetchList();
    } catch (err) {
      toast.error('Greška: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadStored = async (filename) => {
    try {
      const res = await axios.get(`${API}/backup/download/${encodeURIComponent(filename)}`, {
        headers: headers(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Preuzet: ${filename}`);
    } catch (err) {
      toast.error('Greška pri preuzimanju');
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Obrisati backup "${filename}"? Ova akcija je nepovratna.`)) return;
    try {
      await axios.delete(`${API}/backup/${encodeURIComponent(filename)}`, { headers: headers() });
      toast.success('Backup obrisan');
      await fetchList();
    } catch (err) {
      toast.error('Greška pri brisanju');
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) { toast.error('Izaberi backup fajl prvo'); return; }
    if (restoreConfirm !== 'POTVRDJUJEM') {
      toast.error('Upiši tačno: POTVRDJUJEM');
      return;
    }
    if (!window.confirm(
      'PAŽNJA: Ova akcija će ZAMIJENITI sve trenutne podatke u bazi sa onima iz backup-a.\n\n' +
      'Postojeći podaci (klijenti, PDV, dnevnik, sve...) će biti OBRISANI i zamijenjeni.\n\n' +
      'Da li želite da nastavite?'
    )) return;

    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('confirm', 'YES_OVERWRITE');
      const res = await axios.post(`${API}/backup/restore`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      setRestoreResult(res.data);
      toast.success(`Restore završen! ${res.data.restored_collections} kolekcija, ${res.data.restored_documents} dokumenata.`);
      setRestoreFile(null);
      setRestoreConfirm('');
    } catch (err) {
      toast.error('Greška pri restore-u: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="backup-page">
      <div className="flex items-center gap-3">
        <FloppyDisk size={32} weight="duotone" className="text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold">Backup & Restore</h1>
          <p className="text-sm text-slate-500">Sigurnosne kopije svih podataka aplikacije</p>
        </div>
      </div>

      {/* Auto-backup info */}
      <Alert className="bg-blue-50 border-blue-200">
        <ShieldCheck size={20} className="text-blue-600" />
        <AlertDescription className="text-sm">
          <strong>Automatski backup:</strong> Sistem svaki dan u <strong>03:00h</strong> ujutru
          automatski pravi backup svih agencija i čuva ga na serveru. Posljednjih <strong>{retention} backup-a</strong> se čuva (stariji se brišu automatski).
        </AlertDescription>
      </Alert>

      {/* Manual backup */}
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Download size={20} className="text-green-600" /> Manuelni Backup
        </h2>
        <p className="text-sm text-slate-600">
          Kreira potpuni backup baze (svi klijenti, PDV, dnevnik, zahtjevi, podešavanja, Viber/email logovi, multi-tenant podaci) i preuzima ga kao .zip fajl direktno na Vaš računar.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleDownloadNow}
            disabled={downloading}
            data-testid="backup-download-btn"
            className="bg-green-600 hover:bg-green-700"
          >
            {downloading ? (
              <><ArrowClockwise size={18} className="animate-spin mr-2" /> Pravi backup...</>
            ) : (
              <><Download size={18} className="mr-2" /> Preuzmi Backup Sada</>
            )}
          </Button>
          <Button
            onClick={handleSaveToServer}
            disabled={creating}
            variant="outline"
            data-testid="backup-save-server-btn"
          >
            {creating ? (
              <><ArrowClockwise size={18} className="animate-spin mr-2" /> Čuvam...</>
            ) : (
              <><FloppyDisk size={18} className="mr-2" /> Sačuvaj na serveru</>
            )}
          </Button>
        </div>
      </Card>

      {/* Restore */}
      <Card className="p-6 space-y-4 border-red-200 bg-red-50/30">
        <h2 className="text-lg font-semibold flex items-center gap-2 text-red-700">
          <Upload size={20} /> Restore (Vraćanje iz backup-a)
        </h2>
        <Alert className="bg-red-100 border-red-300">
          <Warning size={20} className="text-red-700" />
          <AlertDescription className="text-sm text-red-900">
            <strong>OPASNO:</strong> Restore <strong>kompletno zamjenjuje</strong> trenutne podatke u bazi sa onima iz backup-a.
            Svi postojeći klijenti, PDV, zahtjevi, dnevnik, podešavanja — biće OBRISANI i zamijenjeni. Akcija je nepovratna.
            Preporučujemo da prvo preuzmete trenutni backup prije nego što uradite restore.
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium block mb-1">Backup fajl (.zip)</label>
            <Input
              type="file"
              accept=".zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input"
            />
            {restoreFile && (
              <p className="text-xs text-slate-500 mt-1">
                Izabrano: <strong>{restoreFile.name}</strong> ({formatBytes(restoreFile.size)})
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">
              Da nastavite, upišite riječ: <code className="bg-slate-200 px-2 rounded">POTVRDJUJEM</code>
            </label>
            <Input
              value={restoreConfirm}
              onChange={(e) => setRestoreConfirm(e.target.value)}
              placeholder="Upišite POTVRDJUJEM"
              data-testid="restore-confirm-input"
            />
          </div>
          <Button
            onClick={handleRestore}
            disabled={!restoreFile || restoreConfirm !== 'POTVRDJUJEM' || restoring}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="restore-submit-btn"
          >
            {restoring ? (
              <><ArrowClockwise size={18} className="animate-spin mr-2" /> Vraćam podatke...</>
            ) : (
              <><Upload size={18} className="mr-2" /> Pokreni Restore</>
            )}
          </Button>
        </div>

        {restoreResult && (
          <Alert className="bg-green-100 border-green-300">
            <CheckCircle size={20} className="text-green-700" />
            <AlertDescription className="text-sm">
              <strong>Restore uspješan!</strong> Vraćeno: <strong>{restoreResult.restored_collections}</strong> kolekcija,
              {' '}<strong>{restoreResult.restored_documents}</strong> dokumenata.
              {restoreResult.errors?.length > 0 && (
                <div className="mt-2 text-amber-800">
                  Upozorenja ({restoreResult.errors.length}): {restoreResult.errors.slice(0, 3).join(', ')}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Stored backups list */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FloppyDisk size={20} className="text-blue-600" /> Backup-i na serveru ({backups.length})
          </h2>
          <Button onClick={fetchList} variant="outline" size="sm" data-testid="backup-refresh-btn">
            <ArrowClockwise size={16} className="mr-2" /> Osvježi
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Učitavanje...</p>
        ) : backups.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            Nema backup-a na serveru. Prvi automatski će biti napravljen sutra u 03:00h, ili kliknite &quot;Sačuvaj na serveru&quot; gore.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="px-3 py-2">Ime fajla</th>
                  <th className="px-3 py-2">Tip</th>
                  <th className="px-3 py-2">Veličina</th>
                  <th className="px-3 py-2">Datum</th>
                  <th className="px-3 py-2 text-right">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.filename} className="border-t hover:bg-slate-50" data-testid={`backup-row-${b.filename}`}>
                    <td className="px-3 py-2 font-mono text-xs">{b.filename}</td>
                    <td className="px-3 py-2">
                      {b.is_auto
                        ? <Badge className="bg-blue-100 text-blue-700 border-blue-200">Auto</Badge>
                        : <Badge className="bg-purple-100 text-purple-700 border-purple-200">Manuelni</Badge>}
                    </td>
                    <td className="px-3 py-2">{b.size_mb} MB</td>
                    <td className="px-3 py-2">{formatDate(b.created_at)}</td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <Button
                        onClick={() => handleDownloadStored(b.filename)}
                        size="sm" variant="outline"
                        data-testid={`backup-download-${b.filename}`}
                      >
                        <Download size={14} className="mr-1" /> Preuzmi
                      </Button>
                      <Button
                        onClick={() => handleDelete(b.filename)}
                        size="sm" variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`backup-delete-${b.filename}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
